// One of the owner's own dashboards or configs: its versions (the newest on their MFDs; any downloaded, or made the
// newest again), a new version added from this computer, whom it's shared with (several at once, by email, or no
// longer), and deleting it.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@tune/shared';
import { useId, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError, data, problem } from '../../api';
import { useAuth } from '../../auth';
import { fetchFile, kindWord, size } from '../../items';
import { count, dayText } from '../../lib/words';
import { keys } from '../../queries';
import { Page } from '../../shell/Page';
import {
  Badge,
  Banner,
  Button,
  ButtonLink,
  EmailListField,
  EmptyState,
  isEmail,
  ErrorBanner,
  ListButton,
  ListSection,
  ListValue,
  Menu,
  IconButton,
  Sheet,
  Skeleton,
  useConfirm,
  useRouteSheet,
  useToast,
} from '../../ui';
import { BooksIcon, DotsThreeIcon, PencilRulerIcon, PlusIcon, ShareNetworkIcon, TrashIcon } from '../../ui/icons';
import { AddFileSheet } from './AddFileSheet';
import { ItemThumb, ORIGINS } from './parts';
import s from './Library.module.css';

type Detail = components['schemas']['OwnItemDetail'];
type Version = components['schemas']['Version'];

const BACK = { to: '/library', label: 'Library' };

export function LibraryItem() {
  const { itemId = '' } = useParams();
  const api = useAuth().api('owners');
  const detail = useQuery({
    queryKey: keys.item(itemId),
    queryFn: () => data(api.GET('/library/{itemId}', { params: { path: { itemId } } })),
  });
  if (detail.data) {
    return <ItemPage detail={detail.data} />;
  }
  if (detail.isError && detail.error instanceof ApiError && detail.error.status === 404) {
    return (
      <Page title="No such item" back={BACK}>
        <EmptyState
          icon={<BooksIcon size={56} />}
          title="This isn't in your library"
          action={<ButtonLink to="/library">Go to your library</ButtonLink>}
        >
          It may have been deleted.
        </EmptyState>
      </Page>
    );
  }
  return (
    <Page title="Library" back={BACK}>
      {detail.isError ? (
        <ErrorBanner retry={() => void detail.refetch()}>It couldn't load.</ErrorBanner>
      ) : (
        <Skeleton blocks={2} lines={2} label="Loading it" />
      )}
    </Page>
  );
}

function ItemPage({ detail }: { detail: Detail }) {
  const { item, versions, shares } = detail;
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const say = useToast();
  const [adding, setAdding] = useRouteSheet('add-version');
  const [sharing, setSharing] = useRouteSheet('share');
  const path = { params: { path: { itemId: item.id } } };
  const changed = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.item(item.id) }),
      queryClient.invalidateQueries({ queryKey: keys.library }),
    ]);
  };
  const remove = useMutation({
    mutationFn: () => data(api.DELETE('/library/{itemId}', path)),
    onSuccess: async () => {
      void navigate('/library', { replace: true });
      queryClient.removeQueries({ queryKey: keys.item(item.id) });
      await queryClient.invalidateQueries({ queryKey: keys.library });
      say(`${item.name} is deleted.`);
    },
  });
  const unshare = useMutation({
    mutationFn: (shareId: string) =>
      data(api.DELETE('/library/{itemId}/shares/{shareId}', { params: { path: { itemId: item.id, shareId } } })),
    onSuccess: changed,
  });
  const askDelete = async () => {
    const yes = await confirm({
      title: `Delete ${item.name}?`,
      message:
        shares.length > 0
          ? `Every version goes. Your MFDs, and those of the ${count(shares.length, 'person', 'people')} it's shared with, remove it at their next sync.`
          : 'Every version goes. Your MFDs remove it at their next sync, never while it’s on screen or in use.',
      confirm: 'Delete',
    });
    if (yes) {
      remove.mutate();
    }
  };
  const askUnshare = async (share: Detail['shares'][number]) => {
    const yes = await confirm({
      title: `Stop sharing with ${share.displayName ?? share.email}?`,
      message: `${item.name} goes from their MFDs at their next sync.`,
      confirm: 'Stop sharing',
    });
    if (yes) {
      unshare.mutate(share.id);
    }
  };
  return (
    <Page
      title={item.name}
      back={BACK}
      subtitle={[kindWord(item.kind), item.author ? `by ${item.author}` : null].filter(Boolean).join(' ')}
    >
      {(remove.error ?? unshare.error) && <Banner tone="error">{problem(remove.error ?? unshare.error)}</Banner>}
      <div className={s.hero}>
        <ItemThumb url={item.thumbnailUrl} kind={item.kind} size="large" />
        <div className={s.heroText}>
          {item.description && <p>{item.description}</p>}
          <p className={s.cardMeta}>
            {ORIGINS[item.origin]}, {dayText(item.createdAt)} · updated {dayText(item.updatedAt)}
          </p>
          <div className={s.rowActions}>
            {item.kind === 'config' && (
              <ButtonLink
                to={`/create/configs/${item.id}`}
                size="small"
                icon={<PencilRulerIcon size={16} weight="bold" />}
              >
                Open in the config creator
              </ButtonLink>
            )}
            <Button
              size="small"
              variant={item.kind === 'config' ? 'gray' : 'filled'}
              icon={<PlusIcon size={16} weight="bold" />}
              onClick={() => setAdding(true)}
            >
              Add a new version
            </Button>
            <Button
              size="small"
              variant="gray"
              icon={<ShareNetworkIcon size={16} weight="bold" />}
              onClick={() => setSharing(true)}
            >
              Share
            </Button>
          </div>
        </div>
      </div>
      <div className={s.columns}>
        <ListSection header="Versions" footer="The newest is on your MFDs. Your library keeps the newest five.">
          {versions.map((v, i) => (
            <VersionRow key={v.n} itemId={item.id} version={v} newest={i === 0} onRestored={changed} />
          ))}
        </ListSection>
        <ListSection
          header="Shared with"
          footer="It reaches their MFDs by itself. Someone without a TUNE account is emailed to make one, and gets it then."
        >
          {shares.map((share) => (
            <ListValue
              key={share.id}
              label={share.displayName ?? share.email}
              subtitle={`${share.displayName ? share.email : 'No account yet'} · since ${dayText(share.sharedAt)}`}
              detail={
                <Button
                  variant="destructivePlain"
                  size="small"
                  disabled={unshare.isPending}
                  onClick={() => void askUnshare(share)}
                >
                  Stop sharing<span className="visually-hidden"> with {share.displayName ?? share.email}</span>
                </Button>
              }
            />
          ))}
          <ListButton
            tone="accent"
            icon={<ShareNetworkIcon size={20} weight="bold" />}
            label={shares.length > 0 ? 'Share with more people' : 'Share with someone'}
            onClick={() => setSharing(true)}
          />
        </ListSection>
      </div>
      <ListSection>
        <ListButton
          tone="destructive"
          icon={<TrashIcon size={20} weight="bold" />}
          label="Delete"
          disabled={remove.isPending}
          onClick={() => void askDelete()}
        />
      </ListSection>
      <AddFileSheet
        open={adding}
        onClose={() => setAdding(false)}
        item={{ id: item.id, identity: item.identity, name: item.name }}
      />
      <ShareSheet itemId={item.id} name={item.name} open={sharing} onClose={() => setSharing(false)} />
    </Page>
  );
}

function VersionRow({
  itemId,
  version,
  newest,
  onRestored,
}: {
  itemId: string;
  version: Version;
  newest: boolean;
  onRestored: () => Promise<void>;
}) {
  const api = useAuth().api('owners');
  const confirm = useConfirm();
  const say = useToast();
  const download = useMutation({
    mutationFn: () =>
      data(api.GET('/library/{itemId}/download', { params: { path: { itemId }, query: { n: version.n } } })),
    onSuccess: (link) => fetchFile(link.url),
  });
  const restore = useMutation({
    mutationFn: () =>
      data(api.POST('/library/{itemId}/restore', { params: { path: { itemId } }, body: { n: version.n } })),
    onSuccess: async () => {
      await onRestored();
      say('It’s the newest now. Your MFDs get it at their next sync.');
    },
  });
  const name = version.version ? `Version ${version.version}` : `Version ${version.n}`;
  const ask = async () => {
    const yes = await confirm({
      title: `Make ${name} the newest?`,
      message: 'It goes to your MFDs again, as the newest. The versions after it stay in your library.',
      confirm: 'Make it the newest',
      destructive: false,
    });
    if (yes) {
      restore.mutate();
    }
  };
  return (
    <ListValue
      label={
        <span className={s.version}>
          {name}
          {newest && <Badge tone="accent">On your MFDs</Badge>}
        </span>
      }
      subtitle={`${ORIGINS[version.origin]}, ${dayText(version.createdAt)} · ${size(version.size)}${
        version.minAppVersion ? ` · needs TUNE Live ${version.minAppVersion}` : ''
      }`}
      detail={
        <Menu
          label={name}
          entries={[
            { label: 'Download', onSelect: () => download.mutate() },
            ...(newest ? [] : [{ label: 'Make it the newest', onSelect: () => void ask() }]),
          ]}
          trigger={(props) => (
            <IconButton {...props} label={`More for ${name}`} icon={<DotsThreeIcon size={20} weight="bold" />} />
          )}
        />
      }
    />
  );
}

/** Sharing with several people at once, by email: each is told by email, and gets it on their MFDs. */
function ShareSheet({
  itemId,
  name,
  open,
  onClose,
}: {
  itemId: string;
  name: string;
  open: boolean;
  onClose: () => void;
}) {
  const id = useId();
  const api = useAuth().api('owners');
  const queryClient = useQueryClient();
  const say = useToast();
  const [emails, setEmails] = useState<string[]>([]);
  const [results, setResults] = useState<Array<{ email: string; problem: string | null }>>([]);
  const share = useMutation({
    mutationFn: async (to: string[]) => {
      const out: Array<{ email: string; problem: string | null }> = [];
      for (const email of to) {
        try {
          await data(api.POST('/library/{itemId}/shares', { params: { path: { itemId } }, body: { email } }));
          out.push({ email, problem: null });
        } catch (e) {
          out.push({ email, problem: shareProblem(e) });
        }
      }
      return out;
    },
    onSuccess: async (out) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: keys.item(itemId) }),
        queryClient.invalidateQueries({ queryKey: keys.library }),
      ]);
      const failed = out.filter((r) => r.problem);
      if (failed.length === 0) {
        say(
          out.length === 1
            ? `Shared with ${out[0]?.email}. They've been emailed.`
            : `Shared with ${out.length} people. They've been emailed.`,
        );
        setEmails([]);
        setResults([]);
        onClose();
      } else {
        setResults(out);
        setEmails(failed.map((r) => r.email));
      }
    },
  });
  return (
    <Sheet
      open={open}
      onClose={() => {
        setResults([]);
        share.reset();
        onClose();
      }}
      title={`Share ${name}`}
      action={{
        label: 'Share',
        busy: share.isPending,
        disabled: emails.length === 0 || emails.some((e) => !isEmail(e)),
        onClick: () => share.mutate(emails),
      }}
    >
      <form
        id={id}
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (emails.length > 0 && emails.every(isEmail)) {
            share.mutate(emails);
          }
        }}
        noValidate
      >
        {results.some((r) => r.problem) && (
          <Banner tone="error" title="Some weren't shared">
            <ul className={s.results}>
              {results.map((r) => (
                <li key={r.email}>
                  {r.email}: {r.problem ?? 'shared, and emailed.'}
                </li>
              ))}
            </ul>
          </Banner>
        )}
        <EmailListField
          label="Their emails"
          hint="Several at once, each on its own or separated by commas."
          emails={emails}
          onChange={setEmails}
        />
        <p className={s.note}>
          It reaches their TUNE MFDs by itself. Someone without a TUNE account is emailed to make one, and gets it then.
        </p>
      </form>
    </Sheet>
  );
}

function shareProblem(e: unknown): string {
  if (e instanceof ApiError && e.code === 'quota_exceeded') {
    return "it's shared with as many people as it can be.";
  }
  if (e instanceof ApiError && e.status === 400) {
    return "that isn't an email it can be shared with: not your own, and written like name@example.com.";
  }
  return problem(e).toLowerCase();
}
